// Client-side ZIP bundler for an "Expense report" export. Packs:
//   - expenses.csv:  one row per record with an amount or image attachment
//   - receipts/receipt-NNN.<ext>: every image attachment, renumbered to
//     match the CSV row's "Receipt filename" column for cross-reference
//   - summary.txt:   plain-English totals + date range for the report
//
// Receipt data is sourced from `record.receipt_data` (populated at intake
// by the post-OCR receipt detector — see workers/api/src/lib/receipts.ts).
// Records that have an image but no extracted amount still get a row so
// the user can fill in the missing amount in Excel.
//
// JSZip is lazily imported to avoid bloating the main bundle.
// The exported CSV is BOM-prefixed UTF-8 so Excel renders accents
// correctly without prompting for an encoding.

import type { Case, CaseRecord, RecordAttachment } from "../types";
import { fileUrl } from "../api";

interface Bundle {
  case: Case;
  records: CaseRecord[];
}

interface ParsedReceipt {
  vendor: string | null;
  date: string | null;
  amount: number | null;
  currency: string | null;
  description: string | null;
}

type Category = "meal" | "transport" | "lodging" | "supplies" | "other";

interface ExpenseRow {
  record: CaseRecord;
  date: string;
  vendor: string;
  description: string;
  amount: number | null;
  currency: string;
  category: Category | "";
  receiptFilename: string;
  imageKey: string | null;
  imageMime: string | null;
}

function parseReceiptData(raw: string | null | undefined): ParsedReceipt | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as Record<string, unknown>;
    if (obj.is_receipt !== true) return null;
    const amountRaw =
      typeof obj.amount === "number" ? obj.amount : Number(obj.amount);
    return {
      vendor: typeof obj.vendor === "string" ? obj.vendor : null,
      date:
        typeof obj.date === "string" && /^\d{4}-\d{2}-\d{2}/.test(obj.date)
          ? obj.date.slice(0, 10)
          : null,
      amount: Number.isFinite(amountRaw) && amountRaw > 0 ? amountRaw : null,
      currency:
        typeof obj.currency === "string" ? obj.currency.toUpperCase() : null,
      description: typeof obj.description === "string" ? obj.description : null,
    };
  } catch {
    return null;
  }
}

// First image attachment we can use as a receipt scan. Prefers the legacy
// `image_r2_key` (which is what the receipt detector originally hooked
// into) and falls back to the first image in the attachments array.
function pickImageAttachment(
  r: CaseRecord,
): { key: string; mime: string | null; ext: string } | null {
  if (r.image_r2_key) {
    return {
      key: r.image_r2_key,
      mime: r.file_mime_type,
      ext: extFromMime(r.file_mime_type, r.file_name) ?? "jpg",
    };
  }
  const att = r.attachments?.find((a) =>
    (a.mime_type ?? "").toLowerCase().startsWith("image/"),
  );
  if (att) {
    return {
      key: att.r2_key,
      mime: att.mime_type,
      ext: extFromMime(att.mime_type, att.filename) ?? "jpg",
    };
  }
  return null;
}

function extFromMime(
  mime: string | null | undefined,
  filename: string | null | undefined,
): string | null {
  const fromName = filename?.match(/\.([a-z0-9]{1,5})$/i)?.[1]?.toLowerCase();
  if (fromName) return fromName;
  if (!mime) return null;
  const m = mime.toLowerCase();
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("png")) return "png";
  if (m.includes("webp")) return "webp";
  if (m.includes("heic")) return "heic";
  if (m.includes("gif")) return "gif";
  return null;
}

// Conservative heuristic classifier. The receipt extraction at intake
// doesn't currently classify, and we don't want to fire AI calls inside
// an export, so we keyword-match on vendor + description + subject.
// Anything unmatched falls into "other" — better to under-categorise
// than mislabel.
function classifyCategory(
  vendor: string,
  description: string,
  subject: string | null,
): Category | "" {
  const hay = `${vendor} ${description} ${subject ?? ""}`.toLowerCase();
  if (!hay.trim()) return "";
  if (
    /\b(hotel|inn|motel|hostel|airbnb|lodge|resort|hilton|marriott|hyatt|holiday inn|booking\.com|expedia)\b/.test(
      hay,
    )
  )
    return "lodging";
  if (
    /\b(uber|lyft|taxi|cab|train|rail|amtrak|airline|flight|airfare|gas|fuel|petrol|parking|toll|transit|metro|bus)\b/.test(
      hay,
    )
  )
    return "transport";
  if (
    /\b(restaurant|cafe|coffee|starbucks|mcdonald|lunch|dinner|breakfast|diner|bar|pub|pizza|deli|bistro|food|meal|catering|grubhub|doordash|ubereats)\b/.test(
      hay,
    )
  )
    return "meal";
  if (
    /\b(staples|office depot|amazon|paper|pen|ink|stapler|supplies|printer|notebook|cartridge|toner)\b/.test(
      hay,
    )
  )
    return "supplies";
  return "other";
}

function pickVendor(
  receipt: ParsedReceipt | null,
  r: CaseRecord,
): string {
  if (receipt?.vendor) return receipt.vendor;
  // from_addr is the most reliable "who is this from" signal for emailed
  // receipts; strip an email's display-name wrapper if present.
  if (r.from_addr) {
    const m = r.from_addr.match(/^\s*"?([^"<]+?)"?\s*</);
    return (m?.[1] ?? r.from_addr).trim();
  }
  if (r.subject) return r.subject.slice(0, 60);
  if (r.body_text) return r.body_text.slice(0, 60);
  return "";
}

function pickDescription(
  receipt: ParsedReceipt | null,
  r: CaseRecord,
): string {
  const src =
    r.subject?.trim() ||
    receipt?.description?.trim() ||
    r.body_text?.trim() ||
    "";
  return src.replace(/\s+/g, " ").slice(0, 100);
}

function pickDate(
  receipt: ParsedReceipt | null,
  r: CaseRecord,
): string {
  if (receipt?.date) return receipt.date;
  // record_date is stored as an ISO-ish string; slice to YYYY-MM-DD.
  return (r.record_date ?? "").slice(0, 10);
}

// Records that should appear in the report: any record with an extracted
// amount OR an image attachment. Note: we intentionally include amount-less
// image-only rows so the user has the receipt file in front of them in
// Excel to type a number in.
function buildExpenseRows(records: CaseRecord[]): ExpenseRow[] {
  const rows: ExpenseRow[] = [];
  for (const r of records) {
    const receipt = parseReceiptData(r.receipt_data);
    const image = pickImageAttachment(r);
    const hasAmount = receipt?.amount != null;
    if (!hasAmount && !image) continue;

    const vendor = pickVendor(receipt, r);
    const description = pickDescription(receipt, r);
    rows.push({
      record: r,
      date: pickDate(receipt, r),
      vendor,
      description,
      amount: receipt?.amount ?? null,
      currency: receipt?.currency || "USD",
      category: classifyCategory(vendor, description, r.subject),
      receiptFilename: "", // filled in below once numbering is known
      imageKey: image?.key ?? null,
      imageMime: image?.mime ?? null,
    });
  }
  return rows;
}

// Quote any CSV field that contains the standard problem characters.
// Plain numbers and ISO dates are emitted unquoted so Excel parses
// the Amount column as numeric.
function csvCell(v: string | number | null): string {
  if (v === null || v === undefined || v === "") return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, "0");
}

interface AttachmentFetch {
  buffer: ArrayBuffer | null;
  error: string | null;
}

async function fetchAttachment(
  url: string,
  authHeaders: Record<string, string>,
): Promise<AttachmentFetch> {
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: authHeaders,
    });
    if (!res.ok) {
      return { buffer: null, error: `HTTP ${res.status} ${res.statusText}` };
    }
    return { buffer: await res.arrayBuffer(), error: null };
  } catch (err) {
    return { buffer: null, error: (err as Error).message || "network error" };
  }
}

function formatTotalsByCurrency(rows: ExpenseRow[]): string {
  // Integer-cent accumulation — house standard, no float sums in display
  // or export code.
  const totals = new Map<string, number>();
  for (const row of rows) {
    if (row.amount == null) continue;
    totals.set(
      row.currency,
      (totals.get(row.currency) ?? 0) + Math.round(row.amount * 100),
    );
  }
  if (totals.size === 0) return "$0.00";
  const parts: string[] = [];
  for (const [currency, cents] of totals) {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    }).format(cents / 100);
    parts.push(formatted);
  }
  return parts.join(" + ");
}

function dateRange(rows: ExpenseRow[]): { earliest: string; latest: string } {
  const dates = rows.map((r) => r.date).filter((d) => /^\d{4}-\d{2}-\d{2}/.test(d));
  if (dates.length === 0) return { earliest: "—", latest: "—" };
  dates.sort();
  return { earliest: dates[0]!, latest: dates[dates.length - 1]! };
}

// Returns true if the case has any record that would appear in the
// expense report. Used by CaseView to decide whether to show the
// dropdown option at all.
export function hasExpenseData(records: CaseRecord[]): boolean {
  return buildExpenseRows(records).length > 0;
}

interface MissingFile {
  row: number;
  vendor: string;
  reason: string;
}

export async function exportExpenseReportZip(
  { case: caseData, records }: Bundle,
  authHeaders: Record<string, string>,
): Promise<Blob> {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();

  const rows = buildExpenseRows(records);
  if (rows.length === 0) {
    throw new Error("This case has no expenses or receipt images to export.");
  }

  const numberWidth = Math.max(3, String(rows.length).length);
  const receiptsDir = zip.folder("receipts");
  const missing: MissingFile[] = [];

  // Walk rows in order, assign sequential receipt-NNN filenames, fetch
  // the matching image into the zip. Rows without an image leave the
  // Receipt filename column blank.
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    if (!row.imageKey) continue;
    const ext = extFromMime(row.imageMime, null) ?? "jpg";
    const name = `receipt-${pad(i + 1, numberWidth)}.${ext}`;
    row.receiptFilename = name;
    const url = fileUrl(caseData.id, row.imageKey);
    const { buffer, error } = await fetchAttachment(url, authHeaders);
    if (buffer) {
      receiptsDir?.file(name, buffer);
    } else {
      missing.push({
        row: i + 1,
        vendor: row.vendor || "(unknown)",
        reason: error ?? "unknown",
      });
    }
  }

  const headers = [
    "Date",
    "Vendor",
    "Description",
    "Amount",
    "Currency",
    "Category",
    "Receipt filename",
  ];
  const csvLines: string[] = [headers.map(csvCell).join(",")];
  for (const row of rows) {
    csvLines.push(
      [
        row.date,
        row.vendor,
        row.description,
        row.amount != null ? row.amount.toFixed(2) : "",
        row.currency,
        row.category,
        row.receiptFilename,
      ]
        .map(csvCell)
        .join(","),
    );
  }
  // UTF-8 BOM so Excel reads accented vendor names correctly.
  zip.file("expenses.csv", "﻿" + csvLines.join("\r\n"));

  const { earliest, latest } = dateRange(rows);
  const generatedAt = new Date().toISOString().slice(0, 10);
  const total = formatTotalsByCurrency(rows);
  const summary = [
    `Expense Report: ${caseData.name}`,
    `Period: ${earliest} to ${latest}`,
    `Total expenses: ${total}`,
    `Number of items: ${rows.length}`,
    `Generated by CaseThread on ${generatedAt}`,
    "",
  ].join("\n");
  zip.file("summary.txt", summary);

  if (missing.length > 0) {
    const lines = [
      "CaseThread expense report — missing receipt images",
      "",
      `${missing.length} receipt image${missing.length === 1 ? "" : "s"} could not be downloaded.`,
      "The corresponding row is still present in expenses.csv but the file is absent from receipts/.",
      "",
      "Row | Vendor | Reason",
      "----|--------|-------",
      ...missing.map(
        (m) => `${String(m.row).padStart(3)} | ${m.vendor} | ${m.reason}`,
      ),
    ];
    zip.file("_MISSING_FILES.txt", lines.join("\n"));
  }

  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}
